import React, { useState } from "react";

export function WebForm() {
  const [formData, setFormData] = useState({
    navn: "",
    phone: "",
    email: "",
    beskrivelse: "",
    website_url: "" // Honeypot antispam field
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.navn.trim()) newErrors.navn = "Indtast venligst dit navn.";
    if (!formData.phone.trim()) {
      newErrors.phone = "Indtast venligst dit telefonnummer.";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Indtast venligst din e-mailadresse.";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Indtast venligst en gyldig e-mailadresse.";
    }
    if (!formData.beskrivelse.trim()) newErrors.beskrivelse = "Skriv venligst en kort beskrivelse af din opgave.";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          navn: formData.navn,
          telefon: formData.phone,
          email: formData.email,
          beskrivelse: formData.beskrivelse,
          website_url: formData.website_url
        })
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({
          navn: "",
          phone: "",
          email: "",
          beskrivelse: "",
          website_url: ""
        });
      } else {
        setServerError("Der opstod en fejl ved afsendelse. Prøv venligst igen eller ring direkte til os.");
      }
    } catch (err) {
      console.error("Form submit error:", err);
      setServerError("Netværksfejl. Kontrollér din internetforbindelse og prøv igen.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-[#1e2329] border border-[#c06228]/30 rounded p-8 text-center space-y-4 animate-fadeIn">
        <div className="w-16 h-16 bg-[#c06228]/20 text-[#c06228] rounded-full flex items-center justify-center mx-auto text-2xl">
          <i className="fa-solid fa-check" aria-hidden="true" />
        </div>
        <h3 className="text-2xl font-bold text-white">Mange tak for din henvendelse!</h3>
        <p className="text-gray-300 max-w-md mx-auto">
          Vi har modtaget din besked og vender tilbage til dig så hurtigt som muligt.
        </p>
        <button
          type="button"
          onClick={() => setSubmitted(false)}
          className="btn-cta text-sm font-semibold px-6 py-2.5 rounded inline-block mt-2"
        >
          Send en ny besked
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-0 space-y-5">
      <h3 className="text-xl font-bold text-[#1e2329] pb-2 border-b border-gray-100 flex items-center gap-2">
        <i className="fa-solid fa-paper-plane text-[#c06228]" aria-hidden="true" />
        <span>Få et uforpligtende tilbud</span>
      </h3>

      {serverError && (
        <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm rounded">
          {serverError}
        </div>
      )}

      {/* Honeypot Field (Hidden from humans) */}
      <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
        <label htmlFor="website_url">Website url (lad feltet være tomt)</label>
        <input
          type="text"
          id="website_url"
          name="website_url"
          tabIndex="-1"
          value={formData.website_url}
          onChange={handleChange}
          autoComplete="off"
        />
      </div>

      {/* Navn */}
      <div>
        <label htmlFor="navn" className="block text-sm font-semibold text-gray-800 mb-1">
          Navn <span className="text-[#c06228]">*</span>
        </label>
        <input
          type="text"
          id="navn"
          name="navn"
          value={formData.navn}
          onChange={handleChange}
          placeholder="Dit navn"
          className={`w-full px-4 py-3 bg-gray-50 border ${
            errors.navn ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:border-[#c06228] focus:ring-[#c06228]"
          } rounded text-gray-900 text-sm focus:outline-none focus:ring-2 focus:bg-white transition-all`}
        />
        {errors.navn && <p className="mt-1 text-xs text-red-600">{errors.navn}</p>}
      </div>

      {/* Telefon & Email */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="phone" className="block text-sm font-semibold text-gray-800 mb-1">
            Telefonnummer <span className="text-[#c06228]">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Dit telefonnummer"
            className={`w-full px-4 py-3 bg-gray-50 border ${
              errors.phone ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:border-[#c06228] focus:ring-[#c06228]"
            } rounded text-gray-900 text-sm focus:outline-none focus:ring-2 focus:bg-white transition-all`}
          />
          {errors.phone && <p className="mt-1 text-xs text-red-600">{errors.phone}</p>}
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-semibold text-gray-800 mb-1">
            E-mail <span className="text-[#c06228]">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Din e-mailadresse"
            className={`w-full px-4 py-3 bg-gray-50 border ${
              errors.email ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:border-[#c06228] focus:ring-[#c06228]"
            } rounded text-gray-900 text-sm focus:outline-none focus:ring-2 focus:bg-white transition-all`}
          />
          {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
        </div>
      </div>

      {/* Beskrivelse */}
      <div>
        <label htmlFor="beskrivelse" className="block text-sm font-semibold text-gray-800 mb-1">
          Beskrivelse af projektet <span className="text-[#c06228]">*</span>
        </label>
        <textarea
          id="beskrivelse"
          name="beskrivelse"
          rows="4"
          value={formData.beskrivelse}
          onChange={handleChange}
          placeholder="Beskriv kort din opgave (f.eks. renovering af badeværelse, ny flisebelægning, reparation af murværk eller tilbygning)..."
          className={`w-full px-4 py-3 bg-gray-50 border ${
            errors.beskrivelse ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:border-[#c06228] focus:ring-[#c06228]"
          } rounded text-gray-900 text-sm focus:outline-none focus:ring-2 focus:bg-white transition-all`}
        />
        {errors.beskrivelse && <p className="mt-1 text-xs text-red-600">{errors.beskrivelse}</p>}
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={submitting}
        className="btn-cta w-full py-3.5 px-6 font-semibold text-white rounded text-base transition-all flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {submitting ? (
          <>
            <i className="fa-solid fa-[#c06228] fa-circle-notch animate-spin" aria-hidden="true" />
            <span>Sender forespørgsel...</span>
          </>
        ) : (
          <>
            <span>Send forespørgsel</span>
            <i className="fa-solid fa-arrow-right" aria-hidden="true" />
          </>
        )}
      </button>
    </form>
  );
}
